import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_PAYMENTS_CONFIG } from '~/services';

type SubscriptionContextValue = {
  agentId?: string;
  loading: boolean;
  error?: any;
  paymentsConfig?: any;
  products?: any[];
  subscriptionData?: any;
  activeSubscription?: any;
  hasPaidSubscription: boolean;
  features?: { campaigns: boolean; calendar: boolean };
  refetch: () => void;
};

const SubscriptionContext = createContext<SubscriptionContextValue>({
  agentId: undefined,
  loading: false,
  error: undefined,
  paymentsConfig: undefined,
  products: [],
  subscriptionData: undefined,
  activeSubscription: undefined,
  hasPaidSubscription: false,
  features: undefined,
  refetch: () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const SubscriptionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { agentId } = useParams();

  const { data, loading, error, refetch } = useQuery(GET_PAYMENTS_CONFIG, {
    variables: {
      data: {
        agent_id: agentId,
      },
    },
    skip: !agentId,
    fetchPolicy: 'network-only',
  });

  const value = useMemo<SubscriptionContextValue>(() => {
    const paymentsConfig = data?.get_payments_config?.data;
    const subscriptionData = paymentsConfig?.subscriptionData;
    const activeSubscription = subscriptionData?.active;
    const hasPaidSubscription = !!(
      activeSubscription &&
      activeSubscription.is_active &&
      activeSubscription.stripe_data?.customer_id
    );
    const features = subscriptionData?.features as
      | { campaigns: boolean; calendar: boolean }
      | undefined;

    return {
      agentId,
      loading: agentId ? loading : false,
      error,
      paymentsConfig,
      products: paymentsConfig?.products || [],
      subscriptionData,
      activeSubscription,
      hasPaidSubscription,
      features,
      refetch: () => {
        if (agentId) refetch();
      },
    };
  }, [agentId, data, loading, error, refetch]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
